import { CONFIG } from "~/config";
import { _ } from "~/i18n";

export function dateFormat(date: Date, format = "d. m. yyyy hh:MM:ss", useUTC = false): string {
	const day = date.getDate();
	const month = (useUTC ? date.getUTCMonth() : date.getMonth()) + 1;
	// neni nutno pro utc
	const year = date.getFullYear();
	const seconds = useUTC ? date.getUTCSeconds() : date.getSeconds();
	const minutes = useUTC ? date.getUTCMinutes() : date.getMinutes();
	const hours = useUTC ? date.getUTCHours() : date.getHours();
	const dateObj = {
		yyyy: year,
		m: month,
		mm: month < 10 ? `0${month}` : month,
		d: day,
		dd: day < 10 ? `0${day}` : day,
		s: seconds,
		ss: seconds < 10 ? `0${seconds}` : seconds,
		M: minutes,
		MM: minutes < 10 ? `0${minutes}` : minutes,
		h: hours,
		hh: hours < 10 ? `0${hours}` : hours,
		mn: _(`detail.content.smallMonth.genetiv.${month - 1}`),
	};

	// vystup
	let output = "";
	// aktualni cast
	let part = "";

	for (let index = 0, max = format.length; index < max; index++) {
		let partSection = false;
		let sign = format[index];

		// jedna se o znak?
		if (sign.match(/\w/u)) {
			part += sign;

			// mame neco v part a dosli jsme na konec retezce
			if (index === max - 1) {
				partSection = true;
				sign = "";
			}
		} else {
			partSection = true;
		}

		if (partSection) {
			if (part) {
				output += part in dateObj ? dateObj[part] : part;
			}

			part = "";
			output += sign;
		}
	}

	return output;
}

export function timePassed(date: Date): string {
	const diff = (Date.now() - date.getTime()) / 1000 >>> 0;
	const lang = CONFIG.defaultLang;

	if (diff >= 24 * 3600) {
		return new Intl.DateTimeFormat(lang, { year: "numeric", month: "long", day: "numeric" }).format(date);
	}

	const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });

	return diff >= 3600 ? rtf.format(Math.floor(-diff / 3600), "hour") : rtf.format(Math.floor(-diff / 60), "minute");
}

export function jsonParseCleaner(key: string, value: any): any {
	return key === "__proto__" ? undefined : value;
}

/*
pravidla dle Pau:
Formát času pro aktualizaci a příjezd
nyní = do 1 min
od 1 min do 23h 59 min pak ukazujeme čas - do hodiny v minutách a nad hodinu jako XXh XXmin
24h - 47:59 = včera
48h 7 dní = 2d, 3d, 4d, 5d, 6d
7dní - 4 týdny = 1t, 2t, 3t, 4t
1-12 měsíců = 1m, 2m, 3m, 4m...
víc než 12 měsícú = 1r, 2r atd.
*/
export function timePosition(date: Date): string {
	const rightNowDate = new Date();
	// rozdil v minutach - zaporny znamena cas v minulosti (pred), kladny v budoucnosti (za)
	const diff = Math.floor((date.getTime() - rightNowDate.getTime()) / 1000 / 60);

	// nyni
	if (diff < 1 && diff > -1) {
		return _("time.now");
	// do jedne hodiny pred
	} else if (diff <= -1 && diff > -60) {
		return _("time.past.hours", { hours: 0, minutes: -diff });
	// do jednoho dne pred
	} else if (diff <= -60 && diff > 24 * -60) {
		const hours = Math.floor(-diff / 60);
		const minutes = -diff % 60;

		return _("time.past.hours", { hours, minutes });
	// pred vic nez 1 dnem a mene nez 2 dny (tedy v principu vcera)
	} else if (diff <= 24 * -60 && diff > 2 * 24 * -60) {
		return _("yesterday");
	// pred vic nez 2 dny a mene nez 7 dny
	} else if (diff <= 2 * 24 * -60 && diff > 7 * 24 * -60) {
		const days = Math.floor(-diff / 60 / 24);

		return _("time.past.days", { days });
	// pred vic nez 1 tydnem a mene nez 4 tydny
	} else if (diff <= 7 * 24 * -60 && diff > 28 * 24 * -60) {
		const weeks = Math.floor(-diff / 60 / 24 / 7);

		return _("time.past.weeks", { weeks });
	// pred vic nez 4 tydny (1 mesic) a mene nez 1 rokem
	} else if (diff <= 28 * 24 * -60 && diff > 365 * 24 * -60) {
		const weeks = Math.floor(-diff / 60 / 24 / 7);

		return _("time.past.weeks", { weeks });
	// pred vic nez rokem
	} else if (diff <= 365 * 24 * -60) {
		const years = Math.floor(-diff / 60 / 24 / 365);

		return _("time.past.years", { years });
	// do jedne hodiny po
	} else if (diff >= 1 && diff < 60) {
		return _("time.future.hours", { hours: 0, minutes: diff });
	// do jednoho dne po
	} else if (diff >= 60 && diff < 24 * 60) {
		const hours = Math.floor(diff / 60);
		const minutes = diff % 60;

		return _("time.future.hours", { hours, minutes });
	// za vic nez 1 den a mene nez 2 dny (tedy v principu zitra)
	} else if (diff >= 24 * 60 && diff < 2 * 24 * 60) {
		return _("time.tomorrow");
	// za vic nez 2 dny a mene nez 7 dnu
	} else if (diff >= 2 * 24 * 60 && diff < 7 * 24 * 60) {
		const days = Math.floor(diff / 60 / 24);

		return _("time.future.days", { days });
	// za vic nez 1 tyden a mene nez 4 tydny
	} else if (diff >= 7 * 24 * 60 && diff < 28 * 24 * 60) {
		const weeks = Math.floor(diff / 60 / 24 / 7);

		return _("time.future.weeks", { weeks });
	// za vic nez 4 tydny (1 mesic) a mene nez 1 rok
	} else if (diff >= 28 * 24 * 60 && diff < 365 * 24 * 60) {
		const weeks = Math.floor(diff / 60 / 24 / 7);

		return _("time.future.weeks", { weeks });
	// za vic nez rok
	} else if (diff >= 365 * 24 * 60) {
		const years = Math.floor(diff / 60 / 24 / 365);

		return _("time.future.years", { years });
	}

	return date.toString();
}

export function isoZoneToUTC(isoString: string): Date {
	return new Date(isoString.replace(/((Z)|([+-]\d{2})|([+-]\d{4})|([+-]\d{2}:\d{2}))$/u, "Z"));
}

export function standardDistanceFormat(distance: string, longDecimal = false): {
	distance: string,
	distanceNumber: number,
	unit: string,
	string: string
} {
	const parseDistance = parseFloat(distance);
	const output = {
		distance: "",
		distanceNumber: 0,
		unit: "",
		string: "",
	};

	if (typeof parseDistance !== "number" || isNaN(parseDistance)) {
		return output;
	}

	if (parseDistance < 1000) {
		output.distance = Math.round(parseDistance).toString();
		output.distanceNumber = Math.round(parseDistance);
		output.unit = "m";
		output.string = `${Math.round(parseDistance)}${String.fromCharCode(160)}m`;
	} else {
		let km = parseDistance / 1000;

		km = parseFloat(km.toFixed(longDecimal ? 3 : 1).replace(/\.0$/u, "")); //nulu na konci psát nebudeme

		output.distance = Mapy.Util.formatLocaleNumber(km);
		output.distanceNumber = km;
		output.unit = "km";
		output.string = `${Mapy.Util.formatLocaleNumber(km)}${String.fromCharCode(160)}km`;
	}

	return output;
}

/**
* Naformátuje sekundy na standardní zápis času
* @param seconds Sekundy ke zpracování
* @param [withSeconds=false] Vracet minuty i se sekundami
* @return unit - jednotka, key - klíč pro _i88n, string - naformátovaný čas vč. jednotky
*/
export function standardTimeFormat(seconds: number | string = 0, withSeconds = false): {
	time: string;
	unit: string;
	key: string;
	string: string;
} {
	// eslint-disable-next-line id-length
	const s = Math.round(parseFloat(seconds.toString()));
	let output = {
		time: "",
		unit: "",
		key: "",
		string: "",
	};

	if (typeof s !== "number" || isNaN(s)) {
		return output;
	}

	if (s < 60) { //do minuty
		output = {
			time: s.toString(),
			unit: _("time.short.second"),
			key: "second",
			string: s.toString() + String.fromCharCode(160) + _("time.short.second"),
		};
	} else if (s < 3600) { //do hodiny
		if (withSeconds) {
			const min = Math.floor(s / 60);
			const sec = s % 60;
			const ms = `${min.toString()}:${sec < 10 ? `0${sec}` : sec.toString()}`;

			output = {
				time: ms,
				unit: _("time.long.minute"),
				key: "minute",
				string: ms + String.fromCharCode(160) + _("time.short.minute"),
			};
		} else {
			const minutes = Math.min(Math.round(s / 60), 59).toString();

			output = {
				time: minutes,
				unit: _("time.long.minute"),
				key: "minute",
				string: minutes + String.fromCharCode(160) + _("time.short.minute"),
			};
		}
	} else {
		const hr = Math.floor(s / 3600);
		const min = Math.min(Math.round((s % 3600) / 60), 59);
		const hm = `${hr.toString()}:${min < 10 ? `0${min}` : min.toString()}`;

		output = {
			time: hm,
			unit: _("time.short.hour"),
			key: "hour",
			string: hm + String.fromCharCode(160) + _("time.short.hour"),
		};
	}

	return output;
}

/**
* Převede datum v objektu Date na lokálně formátovaný čas
* @param date objekt Date k převedení na řetězec
* @param showSeconds Vracet včetně sekund?
*/
export function dateToTime(date: Date, showSeconds = false, anotherDay = false): string {
	const hours = date.getUTCHours().toString().padStart(2, "0");
	const minutes = date.getUTCMinutes().toString().padStart(2, "0");
	const seconds = date.getUTCSeconds().toString().padStart(2, "0");
	const day = date.getUTCDate().toString();
	const month = (date.getUTCMonth() + 1).toString();

	if (anotherDay && (Math.abs(date.getTime() - Date.now()) / 86400000) > 1) {
		return `${day}.&nbsp;${month}.&nbsp;${hours}:${minutes}${showSeconds ? `:${seconds}`: ""}`;
	}

	return `${hours}:${minutes}${showSeconds ? `:${seconds}`: ""}`;
}
